import {
  Component,
  OnInit,
  ViewChild,
  AfterViewInit,
  ElementRef,
} from "@angular/core";
import { DataService } from "../../services/data.service";
import { DataType } from "../../models/data-type.model";
import { OnDestroy } from "@angular/core/src/metadata/lifecycle_hooks";
import { MatTableDataSource, MatSort } from "@angular/material";
import { UploaderService } from "../../services/uploader.service";
import { ToastService } from "../../components/toast/toast.service";
import { MetabolomicMetaComponent } from "./modals/metabolomic-meta-modal.component";
import { GuidService } from "../../services/guid.service";
import { SubmitUploadComponent } from "./modals/submit-modal.component";
import { GeneticMetaComponent } from "./modals/genetic-meta-modal.component";
import { ImageMetaComponent } from "./modals/image-meta-modal.component";
import Utils from "../../services/utils";
import { AuthService } from "../../services/auth.service";
import { LoadingSpinnerService } from "../../components/loading-spinner/loading-spinner.service";
import { GradingMetaComponent } from "./modals/grading-meta-modal.component";
import { Bucket } from "../../models/bucket.enum";
import { PatientService } from "../../services/patient.service";
import { UploadFileStatus } from "../../models/upload.model";

@Component({
  templateUrl: "./uploader.component.html",
  styleUrls: ["./uploader.component.scss"],
  providers: [
    DataService,
    UploaderService,
    GuidService,
    AuthService,
    PatientService,
  ],
})
export class UploaderComponent implements OnInit, OnDestroy, AfterViewInit {
  public userRole;

  public dataTypes: Array<DataType>;
  public selectedDataType: DataType;
  public patientId: string;
  public patientIdErr: string;
  public visitId: string;
  public visitIdErr: string;
  public uploadingItems: Array<UploadingFile>;

  public dragCounter: number;

  public incompleteDataSource: MatTableDataSource<any>;
  public incompleteDataColumns: Array<string>;
  public incompleteDataColumnsDef: Array<{
    matColumnDef: string;
    display: string;
  }>;
  public completeDataSource: MatTableDataSource<any>;
  public completeDataColumns: Array<string>;
  public completeDataColumnsDef: Array<{
    matColumnDef: string;
    display: string;
  }>;

  @ViewChild(MatSort) sort: MatSort;

  // raw html elements for input[file] to manipulate
  public fileInput;
  public fileInputForm;

  // meta data modals
  @ViewChild("submitUpload") submitUpload: SubmitUploadComponent;
  // @ViewChild('metabolomicMeta') metabolomicMeta: MetabolomicMetaComponent;
  // @ViewChild('geneticMeta') geneticMeta: GeneticMetaComponent;
  @ViewChild("imageMeta") imageMeta: ImageMetaComponent;
  // @ViewChild('gradingMeta') gradingMeta: GradingMetaComponent;

  constructor(
    private dataService: DataService,
    private el: ElementRef,
    private uploaderService: UploaderService,
    private toastService: ToastService,
    private guidService: GuidService,
    private authService: AuthService,
    private loadingSpinnerService: LoadingSpinnerService,
    private patientService: PatientService
  ) {
    this.uploadingItems = [];
    this.incompleteDataSource = new MatTableDataSource();
    this.incompleteDataSource.data = [];
    this.completeDataSource = new MatTableDataSource();
    this.completeDataSource.data = [];
    this.incompleteDataColumns = [
      "dataType",
      "patientId",
      "fileName",
      "size",
      "delete",
    ];
    this.incompleteDataColumnsDef = [
      { matColumnDef: "dataType", display: " " },
      { matColumnDef: "patientId", display: "Patient ID" },
      { matColumnDef: "fileName", display: "File Name" },
      { matColumnDef: "size", display: "Size" },
      { matColumnDef: "delete", display: " " },
    ];
    this.completeDataColumns = [
      "dataType",
      "patientId",
      "studyId",
      "fileName",
      "imageType",
      "imagePlatform",
      "undo",
      "delete",
    ];
    this.completeDataColumnsDef = [
      { matColumnDef: "dataType", display: " " },
      { matColumnDef: "patientId", display: "Patient ID" },
      { matColumnDef: "studyId", display: "Study ID" },
      { matColumnDef: "fileName", display: "File Name" },
      { matColumnDef: "imageType", display: "Image Type" },
      { matColumnDef: "imagePlatform", display: "Image Platform" },
      { matColumnDef: "size", display: "Size" },
      { matColumnDef: "undo", display: " " },
      { matColumnDef: "delete", display: " " },
    ];
  }

  async ngOnInit() {
    this.fileInput = document.getElementById("fileInput");
    this.fileInputForm = document.getElementById("fileInputForm");
    this.resetForm();

    this.dataTypes = [];
    const dataTypes = await this.dataService.getDataTypes();
    for (let i = 0; i < dataTypes.length; i++) {
      if (dataTypes[i].uploadable) {
        this.dataTypes.push(dataTypes[i]);
      }
      if (dataTypes[i].abbr === "image") {
        // default to image
        this.selectedDataType = dataTypes[i];
      }
    }

    this.dragCounter = 0;
    const dropzoneRef = this.el.nativeElement.querySelector(".dropzone");
    dropzoneRef.addEventListener("dragenter", (e) => {
      this.dragCounter++;
    });
    dropzoneRef.addEventListener("dragleave", () => {
      this.dragCounter--;
    });
    dropzoneRef.addEventListener("drop", (e) => {
      this.uploadFile(e.dataTransfer.files);
      this.dragCounter = 0;
    });
    dropzoneRef.addEventListener("dragend", () => {
      this.dragCounter = 0;
    });
    dropzoneRef.addEventListener("dragexit", () => {
      this.dragCounter = 0;
    });
    // stop drops anywhere but dropzone
    window.addEventListener("dragover", this.preventDefault, false);
    window.addEventListener("drop", this.preventDefault, false);

    this.userRole = await this.authService.getRole();
  }

  ngOnDestroy() {
    window.removeEventListener("dragover", this.preventDefault);
    window.removeEventListener("drop", this.preventDefault);
  }

  ngAfterViewInit() {
    this.incompleteDataSource.sort = this.sort;
    this.completeDataSource.sort = this.sort;
  }

  private preventDefault(e) {
    e.preventDefault();
  }

  // this is not very clean or abstract
  public async uploadFile(files: Array<File>) {
    if (!this.patientId && this.selectedDataType.abbr !== "grading") {
      this.resetForm();
      this.toastService.throwToast("Enter a Patient ID before uploading data");
      return;
    }
    if (this.patientIdErr && this.selectedDataType.abbr !== "grading") {
      this.resetForm();
      this.toastService.throwToast(this.patientIdErr);
      return;
    }
    if (!this.visitId && this.selectedDataType.abbr !== "grading") {
      this.resetForm();
      this.toastService.throwToast("Enter a Visit ID before uploading data");
      return;
    }
    if (this.visitIdErr && this.selectedDataType.abbr !== "grading") {
      this.resetForm();
      this.toastService.throwToast(this.visitIdErr);
      return;
    }
    if (!this.selectedDataType) {
      this.resetForm();
      this.toastService.throwToast("Select a Data Type before uploading data");
      return;
    }

    let patId = this.patientId;
    if (this.selectedDataType.abbr === "grading") {
      patId = "";
    }

    for (const key in files) {
      // not sure why, but weird keys were making it in here
      if (files[key] && !["length", "item"].includes(key)) {
        const guid = this.guidService.getGuid();
        this.uploadingItems.unshift({
          _id: guid,
          fileName: files[key].name,
          size: Utils.formatBytes(files[key].size, 1),
          completed: 0,
          dataType: this.selectedDataType.abbr,
          patientId: patId,
          visitId: this.visitId,
          meta: {},
        });

        const temporaryUploadInfo =
          await this.uploaderService.getTemporaryUploadInfo(guid);
        if (!temporaryUploadInfo || !temporaryUploadInfo.form) {
          this.toastService.throwToast(
            "Select a Data Type before uploading data"
          );
          continue;
        }

        const emitter = this.uploaderService
          .temporaryUpload(files[key], guid, temporaryUploadInfo.form)
          .subscribe((e) => {
            if (typeof e === "number") {
              // progress, update percent complete
              for (let i = 0; i < this.uploadingItems.length; i++) {
                if (this.uploadingItems[i]._id === guid) {
                  this.uploadingItems[i].completed = e;
                }
              }
            } else if (!e) {
              // complete, remove from list and add to incomplete section
              emitter.unsubscribe();
              for (let i = 0; i < this.uploadingItems.length; i++) {
                if (this.uploadingItems[i]._id === guid) {
                  this.incompleteDataSource.data.unshift(
                    this.uploadingItems[i]
                  );
                  this.uploadingItems.splice(i, 1);
                  this.incompleteDataSource._updateChangeSubscription();
                }
              }
            } else {
              // error, display and remove
              this.toastService.throwToast("Error uploading file: " + e);
              emitter.unsubscribe();
              for (let i = 0; i < this.uploadingItems.length; i++) {
                if (this.uploadingItems[i]._id === guid) {
                  this.uploadingItems.splice(i, 1);
                }
              }
            }
          });
        await Utils.sleep(750); // there's something weird with the async callbacks, so we need these two sleeps
      }
    }
    await Utils.sleep(750);
    this.resetForm();
  }

  public deleteUploadingItem(file: UploadingFile) {
    for (let i = 0; i < this.uploadingItems.length; i++) {
      if (this.uploadingItems[i]._id === file._id) {
        this.uploadingItems.splice(i, 1);
        this.uploaderService.deleteTemporaryUpload(file);
      }
    }
  }

  public deleteIncompleteItem(file: UploadingFile) {
    for (let i = 0; i < this.incompleteDataSource.data.length; i++) {
      if (this.incompleteDataSource.data[i]._id === file._id) {
        this.incompleteDataSource.data.splice(i, 1);
        this.incompleteDataSource._updateChangeSubscription();
        this.uploaderService.deleteTemporaryUpload(file);
      }
    }
  }

  public revertCompleteItem(file: UploadingFile) {
    for (let i = 0; i < this.completeDataSource.data.length; i++) {
      if (this.completeDataSource.data[i]._id === file._id) {
        this.completeDataSource.data.splice(i, 1);
        this.completeDataSource._updateChangeSubscription();
        this.incompleteDataSource.data.unshift(file);
        this.incompleteDataSource._updateChangeSubscription();
      }
    }
  }

  public deleteCompleteItem(file: UploadingFile) {
    for (let i = 0; i < this.completeDataSource.data.length; i++) {
      if (this.completeDataSource.data[i]._id === file._id) {
        this.completeDataSource.data.splice(i, 1);
        this.completeDataSource._updateChangeSubscription();
        this.uploaderService.deleteTemporaryUpload(file);
      }
    }
  }

  public openMetaModal(file: UploadingFile) {
    switch (file.dataType) {
      // case 'genetic': this.geneticMeta.open(file); break;
      case "image":
        this.imageMeta.open(file);
        break;
      // case 'metabolomic': this.metabolomicMeta.open(file); break;
      // case 'grading': this.gradingMeta.open(file); break;
    }
  }

  public saveAndNext(file: UploadingFile) {
    for (let i = 0; i < this.incompleteDataSource.data.length; i++) {
      if (this.incompleteDataSource.data[i]._id === file._id) {
        this.incompleteDataSource.data.splice(i, 1);
        this.completeDataSource.data.unshift(file);
        if (file.dataType === "image") {
          file["imageType"] = file.meta["IMAGE_TYPE"];
          file["imagePlatform"] = file.meta["IMAGE_PLATFORM"];
          file["studyId"] = file.meta["STUDY_ID"];
        }
        this.incompleteDataSource._updateChangeSubscription();
        this.completeDataSource._updateChangeSubscription();
        // loop through all pending incomplete items
        if (this.incompleteDataSource.data.length === 0) {
          return;
        } else if (i >= this.incompleteDataSource.data.length) {
          this.openMetaModal(this.incompleteDataSource.data[0]);
        } else {
          this.openMetaModal(this.incompleteDataSource.data[i]);
        }
        return;
      }
    }
  }

  public getModalSaveText(): string {
    if (this.incompleteDataSource.data.length > 1) {
      return "save and next";
    } else {
      return "save and finish";
    }
  }

  public async patientIdBlur() {
    this.patientIdErr = null;
    if (!this.patientId || this.patientId === "") {
      this.patientIdErr = "Patient ID is required for all uploads";
    } else {
      try {
        this.patientId = await this.patientService.testAndNormalizePatientId(
          this.patientId
        );
      } catch (err) {
        this.patientIdErr = err.message || "Invalid Patient ID";
      }
    }
  }

  public async visitIdBlur() {
    this.visitIdErr = null;
    if (!this.visitId || this.visitId === "") {
      this.visitIdErr = "Visit ID is required for all uploads";
    } else {
      try {
        this.visitId = await this.patientService.testAndNormalizeVisitId(
          this.visitId
        );
      } catch (err) {
        this.visitIdErr = err.message || "Invalid Visit ID";
      }
    }
  }

  public async submit(uploadComments: string) {
    let didUpload = true;
    this.loadingSpinnerService.start();
    for (let i = 0; i < this.completeDataSource.data.length; i++) {
      try {
        this.completeDataSource.data[i]["meta"]["UPLOAD_COMMENTS"] =
          uploadComments;

        const existingUpload =
          await this.uploaderService.checkIfReplaceExisting(
            this.completeDataSource.data[i]
          );
        // if pendingApproval, reject and toast
        // if denied, it's pending a resubmit
        if (
          existingUpload &&
          existingUpload.status === UploadFileStatus.pendingApproval
        ) {
          this.toastService.throwToast(
            "Invalid upload, you are trying to submit for same details as a Pending upload"
          );
          didUpload = false;
        } else {
          const newUpload = await this.uploaderService.submit(
            this.completeDataSource.data[i]
          );
          if (
            existingUpload &&
            existingUpload.status === UploadFileStatus.denied
          ) {
            this.uploaderService.saveResubmission(
              existingUpload.uploadId,
              newUpload.uploadId
            );
          }
        }
      } catch (err) {
        this.loadingSpinnerService.stop();
        this.toastService.throwToast(err.message);
      }
    }
    this.completeDataSource.data = [];
    this.completeDataSource._updateChangeSubscription();
    // this.geneticMeta.clearSaved();
    // this.metabolomicMeta.clearSaved();
    this.imageMeta.clearSaved();
    this.loadingSpinnerService.stop();
    if (didUpload) {
      this.toastService.throwToast("Data successfully uploaded");
    }
  }

  private resetForm() {
    this.fileInput.value = null;
    this.fileInputForm.reset();
  }
}

export class UploadingFile {
  _id?: string;
  fileName?: string;
  size?: string;
  completed?: number;
  dataType?: string;
  patientId: string;
  visitId?: string;
  meta?: Object;
}



// WEBPACK FOOTER //
// ./src/app/home/uploader/uploader.component.ts